"use client";

import { Button } from "@/shared/components/ui/button";
import { Menu, User } from "lucide-react";
import { useRouter, usePathname } from "next/navigation";
import Link from "next/link";
import React, { useState } from "react";
import { useUser } from "@/context/UserContext";
import LanguageSwitcher from "./LanguageSwitcher";
import NotificationBell from "./NotificationBell";
import { useLocale, useTranslations } from "next-intl";
import { useLogout } from "@/lib/hooks/useCompany";

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, isLoggedIn, logout } = useUser();
  const logoutMutation = useLogout();

  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale()

  const isOnTenderPage = pathname.startsWith("/en/tender");

  const userName = user ? `${user.name} ${user.surname}` : "Guest";
  const t = useTranslations("header");

  const handleLogout = () => {
    logoutMutation.mutate({})
  }

  // Handle logout success/error
  React.useEffect(() => {
    if (logoutMutation.isSuccess) {
      // Call the context logout function to clear user state
      logout();
      router.push(`/${locale}/login`);
    }
    if (logoutMutation.isError) {
      logout();
      router.push(`/${locale}/login`);
    }
  }, [logoutMutation.isSuccess, logoutMutation.isError, logout, router, locale]);
  return (
    <header className="z-20 w-full py-6 bg-brand-cream border-b border-brand-dot-track sticky top-0">
      <div className="container px-4 mx-auto flex justify-between items-center">
        {/* Logo */}
        <div className="flex items-center">
          <Link href={`/${locale}`} className="text-xl font-bold text-brand-navy">
            Tender
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md_lg:flex items-center space-x-8">
          <Link href={`/${locale}/#about`} className="text-brand-muted-light hover:text-brand-navy">
            {t("about")}
          </Link>
          <Link href={`/${locale}/#contact`} className="text-brand-muted-light hover:text-brand-navy">
            {t("contact")}
          </Link>
          <Link href={`/${locale}/#faq`} className="text-brand-muted-light hover:text-brand-navy">
            {t("faq")}
          </Link>
          <Link
            href={`/${locale}/#testimonials`}
            className="text-brand-muted-light hover:text-brand-navy"
          >
            {t("testimonials")}
          </Link>
          <Link href={`/${locale}/news`} className="text-brand-muted-light hover:text-brand-navy">
            {t("news")}
          </Link>
        </nav>

        {/* Şrift seçimi hələlik gizlədilib — login/register-dən keçibsə şəxsi kabinet düyməsi */}
        {isLoggedIn && (
          <Button
            variant="outline"
            className="hidden md_lg:flex items-center gap-2 border-brand-dot-track bg-brand-cream text-brand-navy hover:bg-brand-sand hover:text-brand-navy focus-visible:ring-brand-navy focus-visible:ring-offset-brand-cream"
            onClick={() => router.push(`/${locale}/profile`)}
          >
            <User className="h-4 w-4" />
            {t("myCabinet")}
          </Button>
        )}

        {/* CTA / User Info Buttons */}
        <div className="hidden md_lg:flex items-center space-x-4">
          <LanguageSwitcher />
          
          {isLoggedIn ? (
            <div className="flex items-center space-x-4">
              <div 
                className="flex items-center space-x-2 cursor-pointer hover:text-brand-navy transition-colors"
                onClick={() => router.push(`/${locale}/profile`)}
              >
                <User className="h-5 w-5 text-brand-muted-light" />
                <span className="text-brand-muted-light">{userName}</span>
              </div>
              <Button
                variant="outline"
                className="border-brand-dot-track bg-brand-cream text-brand-muted-light hover:bg-brand-sand hover:text-brand-navy focus-visible:ring-brand-navy focus-visible:ring-offset-brand-cream"
                onClick={handleLogout}
                disabled={logoutMutation.isPending}
              >
                {logoutMutation.isPending ? t("loggingOut") : t("logout")}
              </Button>
            </div>
          ) : (
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                className="border-brand-dot-track bg-brand-cream text-brand-muted-light hover:bg-brand-sand hover:text-brand-navy focus-visible:ring-brand-navy focus-visible:ring-offset-brand-cream"
                onClick={() => router.push(`/${locale}/register`)}
              >
                {t("register")}
              </Button>
              <Button
                className="bg-brand-navy text-brand-cream hover:bg-brand-navy-2 focus-visible:ring-brand-navy focus-visible:ring-offset-brand-cream"
                onClick={() => router.push(`/${locale}/login`)}
              >
                {t("login")}
              </Button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {isLoggedIn && <NotificationBell />}

          {/* Mobile menu button */}
          <button
            className="md_lg:hidden p-2 rounded-md text-brand-muted-light"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md_lg:hidden h-screen p-4 pt-2 bg-brand-cream border-t border-brand-dot-track">
          <nav className="flex flex-col space-y-4">
            <Link
              href={`/${locale}/#about`}
              onClick={() => setIsMenuOpen(false)}
              className="text-brand-muted-light hover:text-brand-navy py-2"
            >
              {t("about")}
            </Link>
            <Link
              href={`/${locale}/#contact`}
              onClick={() => setIsMenuOpen(false)}
              className="text-brand-muted-light hover:text-brand-navy py-2"
            >
              {t("contact")}
            </Link>
            <Link
              href={`/${locale}/#faq`}
              onClick={() => setIsMenuOpen(false)}
              className="text-brand-muted-light hover:text-brand-navy py-2"
            >
              {t("faq")}
            </Link>
            <Link
              href={`/${locale}/#testimonials`}
              onClick={() => setIsMenuOpen(false)}
              className="text-brand-muted-light hover:text-brand-navy py-2"
            >
              {t("testimonials")}
            </Link>
            <Link
              href={`/${locale}/news`}
              onClick={() => setIsMenuOpen(false)}
              className="text-brand-muted-light hover:text-brand-navy"
            >
              {t("news")}
            </Link>
            <div className="pt-4 flex flex-col space-y-3">
              <LanguageSwitcher />

              {isLoggedIn ? (
                <div className="flex flex-col space-y-3">
                  <div 
                    className="flex items-center space-x-2 cursor-pointer hover:text-brand-navy transition-colors p-2 border rounded-md"
                    onClick={() => {
                      router.push(`/${locale}/profile`)
                      setIsMenuOpen(false)
                    }}
                  >
                    <User className="h-5 w-5 text-brand-muted-light" />
                    <span className="text-brand-muted-light">{userName}</span>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full justify-center border-brand-dot-track bg-brand-cream text-brand-muted-light hover:bg-brand-sand hover:text-brand-navy focus-visible:ring-brand-navy focus-visible:ring-offset-brand-cream"
                    onClick={handleLogout}
                    disabled={logoutMutation.isPending}
                  >
                    {logoutMutation.isPending ? t("loggingOut") : t("logout")}
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col space-y-3">
                  <Button
                    variant="outline"
                    className="w-full justify-center border-brand-dot-track bg-brand-cream text-brand-muted-light hover:bg-brand-sand hover:text-brand-navy focus-visible:ring-brand-navy focus-visible:ring-offset-brand-cream"
                    onClick={() => router.push(`/${locale}/register`)}
                  >
                    {t("register")}
                  </Button>
                  <Button
                    className="w-full justify-center bg-brand-navy text-brand-cream hover:bg-brand-navy-2 focus-visible:ring-brand-navy focus-visible:ring-offset-brand-cream"
                    onClick={() => router.push(`/${locale}/login`)}
                  >
                    {t("login")}
                  </Button>
                </div>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
